/**
 * SIGN OUT HAS TO SIGN OUT BOTH SESSIONS, because a promoted Jr Staff
 * dancer holds two.
 *
 * A promoted Jr Staff signs in once and ends up with an HttpOnly dancer
 * cookie AND a real Firebase principal (dancerAuth.mjs mints the token,
 * jrStaffDancerSession.ts exchanges it). Her users doc reads role
 * 'ensemble', status 'active' - deliberately identical to an adult Jr Staff
 * account, which is the whole parity ruling.
 *
 * That identical shape is what made clearing only the cookie dangerous.
 * With the cookie gone, App.tsx stops treating the tab as a dancer session,
 * and its retired-dancer-login gate reads isDancerLoginUserDoc, which
 * excludes every staff role - so an {ensemble, active} doc sails past it and
 * the FULL STAFF APP mounts as her, for whoever picks the device up next.
 * On a studio front-desk tablet that is the whole student list with dates of
 * birth and guardian links, every injury note and every task. The Firebase
 * sign-out also carries the teardown nothing else does (lib/auth.ts
 * signOut): the push token comes off this device, and Firestore's per-ORIGIN
 * IndexedDB cache is terminated and wiped, so what she loaded cannot be read
 * back by the next person on the same browser.
 *
 * WHY A SEPARATE MODULE. The decision is small and the consequence of
 * getting it wrong is the entire staff app, so it is written once, here,
 * where a test can execute it - rather than inline in a React callback where
 * only a source grep could ever check it. dancer-session-context.tsx holds
 * no logout logic of its own; it hands this function its dependencies.
 *
 * ORDER IS LOAD-BEARING. The real signOut ends in window.location.reload(),
 * so anything after it never runs: the cookie has to be cleared first. And
 * a failed cookie clear must NOT go on to sign out of Firebase - the dancer
 * session is still live in that case, the caller restores the signed-in
 * state, and half-tearing-down would strand her between two states.
 */

export interface DancerLogoutDeps {
  /** The dancer session's CSRF token, or null when there is no live cookie. */
  csrfToken: string | null;
  /**
   * Is there a Firebase principal on this tab at all? Deliberately NOT "is
   * the jrStaffDancer claim readable": an unreadable or slow claim must not
   * be the reason a staff session survives a sign-out. Under a dancer
   * session the only Firebase principal that is ever allowed to coexist is
   * the promoted one (DancerSessionProvider's own status effect signs the
   * dancer out of the cookie otherwise), so "present" is the safe test and
   * the fail-closed one.
   */
  hasFirebaseSession: boolean;
  /** Clears the server-owned dancer cookie. */
  clearDancerCookie: (csrfToken: string) => Promise<unknown>;
  /**
   * The SAME sign-out the staff app uses (lib/auth.ts), never a bare
   * Firebase signOut: the push detach and the Firestore cache wipe are the
   * half that protects a shared device, and a second spelling here would
   * drift from it silently.
   */
  signOutFirebase: () => Promise<void>;
}

export interface DancerLogoutOutcome {
  /** The dancer cookie was actively cleared on the server. */
  cookieCleared: boolean;
  /** The Firebase principal was signed out, with its full teardown. */
  firebaseSignedOut: boolean;
}

export async function runDancerLogout(deps: DancerLogoutDeps): Promise<DancerLogoutOutcome> {
  let cookieCleared = false;
  if (deps.csrfToken) {
    // Throws on failure, on purpose: the caller keeps the person signed in
    // and shows the error rather than reporting a sign-out that did not
    // happen. Nothing below runs.
    await deps.clearDancerCookie(deps.csrfToken);
    cookieCleared = true;
  }
  if (!deps.hasFirebaseSession) return { cookieCleared, firebaseSignedOut: false };
  await deps.signOutFirebase();
  return { cookieCleared, firebaseSignedOut: true };
}
